import { RemediationRunStatus } from '@libs/constants';

/**
 * What changed between two ticks of the hub's status poll.
 *
 * Three separate answers rather than one "something changed" flag, because the
 * three call for different work and conflating them is expensive in both
 * directions: refetching the tenant's whole run history when a badge merely
 * moved, or patching a badge when a run has actually left the list.
 */
export interface RunStatusDiff {
  /**
   * A run stopped being worked on — it finished, failed, or became the user's
   * turn to review.
   *
   * Detected as an absence, because the endpoint returns only the runs the worker
   * is working on. This is the one that needs a full re-read: what a run turned
   * into lives in counts and titles the status poll deliberately does not carry.
   */
  settled: boolean;
  /** A run started being worked on — here, in another tab, or by another user. */
  appeared: boolean;
  /**
   * Membership held, but at least one run moved between working statuses
   * (e.g. PENDING → PROCESSING). Patchable in place.
   */
  moved: boolean;
}

/**
 * Compare two snapshots of "which run is at which status".
 *
 * Keyed on run id rather than on counts: a tick that starts one run and settles
 * another leaves the size unchanged, and a size comparison would call that
 * quiet. `settled` and `appeared` are reported independently, so a caller that
 * sees both can re-read once and cover them together.
 */
export function diffRunStatuses(
  before: ReadonlyMap<string, RemediationRunStatus>,
  after: ReadonlyMap<string, RemediationRunStatus>,
): RunStatusDiff {
  let settled = false;
  let moved = false;

  for (const [runId, status] of before) {
    const now = after.get(runId);
    if (now === undefined) settled = true;
    else if (now !== status) moved = true;
  }

  let appeared = false;
  for (const runId of after.keys()) {
    if (!before.has(runId)) {
      appeared = true;
      break;
    }
  }

  return { settled, appeared, moved };
}

/**
 * Whether a run that stopped being worked on could have forked an assessment
 * version.
 *
 * Only finalizing does that — it renders the accepted documents and forks version
 * N+1 — so it is the only way of leaving that makes the *assessment* rows on
 * screen wrong. A run that left from PENDING or PROCESSING went to the reviewer
 * or failed, and either way the assessment it came from is untouched, so
 * refetching it would be work for an answer that cannot have changed.
 *
 * Read from the *previous* snapshot: by the time a run matters here it is already
 * gone from the current one, so its last known status is the only evidence of how
 * it left.
 */
export function mayHaveForkedVersion(
  before: ReadonlyMap<string, RemediationRunStatus>,
  after: ReadonlyMap<string, RemediationRunStatus>,
): boolean {
  for (const [runId, status] of before) {
    if (!after.has(runId) && status === RemediationRunStatus.FINALIZING) return true;
  }
  return false;
}
